import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type NotificationCategory =
  | "reservation" | "class" | "no_show" | "schedule_change"
  | "payment" | "invoice" | "monthly_billing"
  | "tournament" | "ranking" | "badge" | "match_result"
  | "approval" | "invite" | "claim" | "system" | "social";

export interface NotificationRow {
  id: string;
  recipient_person_id: string | null;
  recipient_user_id: string | null;
  arena_id: string | null;
  category: NotificationCategory;
  priority: "low" | "normal" | "high" | "urgent";
  title: string;
  body: string | null;
  action_url: string | null;
  source_event_type: string | null;
  source_event_id: string | null;
  metadata: Record<string, unknown> | null;
  read_at: string | null;
  archived_at: string | null;
  created_at: string;
}

interface UseNotificationsOptions {
  arenaId?: string;
  category?: NotificationCategory;
  includeArchived?: boolean;
  limit?: number;
}

export function useNotifications(opts: UseNotificationsOptions = {}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { arenaId, category, includeArchived = false, limit = 50 } = opts;

  const queryKey = ["notifications", user?.id, arenaId ?? null, category ?? null, includeArchived, limit];

  const query = useQuery({
    queryKey,
    enabled: !!user,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      let q = supabase
        .from("notifications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);

      // Filtro explícito por destinatário evita depender apenas de RLS
      // (RLS ainda é aplicada; isto reduz linhas varridas e melhora uso de índice).
      if (arenaId) {
        q = q.eq("arena_id", arenaId);
      } else if (user?.id) {
        q = q.eq("recipient_user_id", user.id);
      }
      if (!includeArchived) q = q.is("archived_at", null);
      if (category) q = q.eq("category", category);

      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as NotificationRow[];
    },
  });

  const unreadCountQuery = useQuery({
    queryKey: ["notifications-unread-count", user?.id],
    enabled: !!user,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_unread_notifications_count");
      if (error) throw error;
      return (data ?? 0) as number;
    },
  });

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("mark_notification_read", { _notification_id: id });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["notifications-unread-count"] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("mark_all_notifications_read");
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["notifications-unread-count"] });
    },
  });

  const archive = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("archive_notification", { _notification_id: id });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["notifications-unread-count"] });
    },
  });

  // Realtime
  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`notifications-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications" },
        () => {
          qc.invalidateQueries({ queryKey: ["notifications"] });
          qc.invalidateQueries({ queryKey: ["notifications-unread-count"] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [user, qc]);

  return {
    notifications: query.data || [],
    isLoading: query.isLoading,
    unreadCount: unreadCountQuery.data ?? 0,
    markRead: markRead.mutate,
    markAllRead: markAllRead.mutate,
    archive: archive.mutate,
  };
}
